import VueWrapper from '@/components/core/Vue/vue.wrapper';
import {InspectionFormQuestionModel, InspectionQuestionFieldOptionsModel, INSPECTION_QUESTION_OPTION_TYPES} from '@/sdk';
import {InspectionFormQuestionAnswerTableModel} from '@/sdk/models/inspection-form-question-answer-table.model';
import {Component, Prop, Watch} from 'vue-property-decorator';
import {JobService, JobsInspectionFormsService} from '../services';

export interface AssessmentResult {
    passScorePercent: number;
    failScore: number;
    level: 'Very Good' | 'Good' | 'Average' | 'Caution' | 'Bad' | 'Very Bad' | 'N/A';
}

async function calculateOverallAssessment(
    questions: InspectionFormQuestionModel[],
    answers: InspectionFormQuestionAnswerTableModel[]
): Promise<AssessmentResult> {
    let totalApplicablePerfectScore = 0;
    let totalPassScore = 0;
    let totalFailScore = 0;

    for (const answer of answers) {
        const question = questions.find(q => q.id === answer.inspectionFormQuestionsTableId);

        if (!question?.inspectionQuestionFieldOptions) {
            continue;
        }

        const options = question.inspectionQuestionFieldOptions;
        const answerText = options.find(opt => opt.id === answer.answer)?.option;
        const serverityText = options.find(opt => opt.id === answer.severity)?.option;

        if (answerText && (answerText.toLowerCase() === 'n/a' || answerText.toLowerCase() === 'na')) {
            continue;
        }

        totalApplicablePerfectScore += 10;

        let actualScoreText = '10';

        if (serverityText?.toLowerCase() === 'n/a' || serverityText?.toLowerCase() === 'na') {
            actualScoreText = '10';
        } else {
            actualScoreText = serverityText ?? '10';
        }

        const actualScore = parseInt(actualScoreText, 10);

        if (answerText?.toLowerCase() === 'pass') {
            if (!isNaN(actualScore)) {
                totalPassScore += actualScore;
            }
        } else if (answerText?.toLowerCase() === 'fail') {
            if (!isNaN(actualScore)) {
                totalFailScore += actualScore;
            }
        }
    }

    if (totalApplicablePerfectScore === 0) {
        return {
            passScorePercent: 100,
            failScore: 0,
            level: 'Very Good',
        };
    }

    let passScorePercent: number;
    let level: AssessmentResult['level'];

    if (totalFailScore > 0) {
        passScorePercent = 0;

        if (totalFailScore >= 40) {
            level = 'Very Bad';
        } else if (totalFailScore >= 20) {
            level = 'Bad';
        } else {
            level = 'Caution';
        }
    } else {
        passScorePercent = (totalPassScore / totalApplicablePerfectScore) * 100;

        if (passScorePercent >= 95) {
            level = 'Very Good';
        } else if (passScorePercent >= 90) {
            level = 'Good';
        } else {
            level = 'Average';
        }
    }

    return {
        passScorePercent: parseFloat(passScorePercent.toFixed(2)),
        failScore: totalFailScore,
        level: level,
    };
}

@Component
export default class SingleInspectionFormComponent extends VueWrapper {
    @Prop({
        required: false,
    })
    gradeOfInspection!: Array<string>;

    @Prop({
        type: String,
        required: true,
    })
    public readonly inspectionFormId!: string;

    public jobInspectionFormsSrv: JobsInspectionFormsService = new JobsInspectionFormsService();
    public jobSrv: JobService = new JobService();

    public overallAssessmentResult: AssessmentResult | null = null;

    public get inspectionForm() {
        return this.jobInspectionFormsSrv.findInspectionFormById(this.inspectionFormId);
    }

    public get inspectionFormQuestions(): InspectionFormQuestionModel[] {
        return this.jobInspectionFormsSrv.inspectionFormQuestionsAgainsJob[this.inspectionFormId] ?? [];
    }

    @Watch('inspectionFormQuestions', {immediate: true, deep: true})
    async onInspectionDataChanged(newQuestions: InspectionFormQuestionModel[]) {
        if (!newQuestions || newQuestions.length === 0) {
            this.overallAssessmentResult = null;
            return;
        }

        const answers = newQuestions
            .map(q => q.inspectionFormQuestionAnswers?.[0])
            .filter((ans): ans is InspectionFormQuestionAnswerTableModel => !!ans && typeof ans.answer === 'string' && typeof ans.severity === 'string');

        if (answers.length === 0) {
            this.overallAssessmentResult = null;
            return;
        }

        this.overallAssessmentResult = await calculateOverallAssessment(newQuestions, answers);
    }

    public getInspectionResultOptions(options: InspectionQuestionFieldOptionsModel[]) {
        return options.filter(x => x.type === INSPECTION_QUESTION_OPTION_TYPES.INSPECTION_RESULT);
    }

    public getInspectionSeverityOptions(options: InspectionQuestionFieldOptionsModel[]) {
        return options.filter(x => x.type === INSPECTION_QUESTION_OPTION_TYPES.INSPECTION_SEVERITY);
    }

    public saveInspectionForm() {
        const toSave = this.inspectionFormQuestions
            .map(x => {
                return {
                    inspectionFormQuestionsTableId: x.id,
                    answer: x.inspectionFormQuestionAnswers?.[0]?.answer ?? null,
                    severity: x.inspectionFormQuestionAnswers?.[0]?.severity ?? null,
                    comment: x.inspectionFormQuestionAnswers?.[0]?.comment ?? null,
                    jobId: this.jobSrv.selectedJob ? this.jobSrv.selectedJob.id : null,
                    id: x.inspectionFormQuestionAnswers?.[0]?.id ?? null,
                };
            })
            ?.filter(x => x.answer !== null || x.severity !== null || !!x.id);

        if (toSave.length === 0) {
            return;
        }

        this.LoaderSrv.showFullScreenLoader();
        this.jobInspectionFormsSrv.inspectionFormQuestionsApi
            .saveQuestionAnswers(this.inspectionFormId!, toSave)
            .then(() => {
                this.jobInspectionFormsSrv.loadInspectionFormQuestionsAgainsJob(
                    this.inspectionFormId!,
                    this.jobSrv.selectedJob!.id!,
                    this.UserSessionSrv.userId!
                );
                this.AlertSrv.show('success', this.$t('SiteComponents.SingleInspectionForm.Success') as string);
            })
            .catch(() => {
                this.AlertSrv.show('error', this.$t('SiteComponents.SingleInspectionForm.Error') as string);
            })
            .finally(() => {
                this.LoaderSrv.hideFullScreenLoader();
            });
    }
}
